import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import { getPost, BLOG_POSTS } from "@/data/blogPosts";
import { useSeo } from "@/lib/useSeo";
import NotFound from "@/pages/NotFound";

const LOGO = "/assets/logo.png";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  useSeo({
    title: post ? `${post.title} | Social Media Combo` : undefined,
    description: post ? post.excerpt : undefined,
    path: post ? `/blog/${slug}` : undefined,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="post-home-link">
            <img src={LOGO} alt="Social Media Combo logo" className="w-8 h-8 object-contain" />
            <span className="font-serif-display text-base tracking-[0.25em] font-semibold">
              THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
            </span>
          </Link>
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-14 md:py-20" data-testid="blog-post">
        <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" className="mb-8">
          <ol className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <li>
              <Link to="/" className="hover:text-[#D4AF37] transition-colors" data-testid="breadcrumb-home">Home</Link>
            </li>
            <li aria-hidden="true" className="text-zinc-700">›</li>
            <li>
              <Link to="/blog" className="hover:text-[#D4AF37] transition-colors" data-testid="breadcrumb-blog">Blog</Link>
            </li>
            <li aria-hidden="true" className="text-zinc-700">›</li>
            <li aria-current="page" className="text-zinc-300 truncate max-w-[60vw] sm:max-w-xs">
              {post.title}
            </li>
          </ol>
        </nav>

        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span className="uppercase tracking-widest text-[#D4AF37]">{post.tag}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(post.date)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
        </div>

        <h1 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" data-testid="post-title">
          {post.title}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-10 aspect-[16/9] overflow-hidden border border-zinc-800 bg-[#1C1C1E]"
        >
          <img src={post.cover} alt={post.coverAlt} className="w-full h-full object-cover" />
        </motion.div>

        <div
          className="blog-prose mt-12"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA */}
        <div className="mt-16 border border-[#D4AF37]/30 bg-[#111111] p-8 text-center">
          <h3 className="font-serif-display text-2xl font-semibold">
            Ready to turn attention into income?
          </h3>
          <p className="mt-3 text-zinc-400 max-w-lg mx-auto">
            Get all 11 faceless marketing ebooks + 3 free bonuses and start building your passive-income empire today.
          </p>
          <Link to="/" data-testid="post-cta-button" className="btn-gold inline-flex items-center gap-2 mt-6 px-8 py-4 text-sm">
            Get The Faceless Bundle <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <Link to="/blog" className="inline-flex items-center gap-2 mt-12 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to all posts
        </Link>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="border-t border-zinc-900" data-testid="related-posts">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37]">Keep reading</p>
            <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl font-semibold">Related posts</h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((rp, i) => (
                <motion.div
                  key={rp.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  data-testid={`related-post-${i}`}
                >
                  <Link
                    to={`/blog/${rp.slug}`}
                    className="group block h-full bg-[#121212] border border-zinc-800 hover:border-[#D4AF37]/50 transition-colors overflow-hidden"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-[#1C1C1E]">
                      <img
                        src={rp.cover}
                        alt={rp.coverAlt}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span className="uppercase tracking-widest text-[#D4AF37]">{rp.tag}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {rp.readTime}</span>
                      </div>
                      <h3 className="mt-3 font-serif-display text-lg font-semibold leading-snug group-hover:text-[#D4AF37] transition-colors">
                        {rp.title}
                      </h3>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#D4AF37]">
                        Read <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-600">
          <span className="font-serif-display tracking-widest">SOCIAL MEDIA COMBO.</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
